import MatterNotes from "@/components/features/matter/notes/MatterNotes";
import MatterTasks from "@/components/features/matter/tasks/MatterTasks";
import {
  AppModal,
  ContextMenu,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  SearchableContextMenuSubmenu,
} from "@/components/ui";
import { useUser } from "@/components/UserProvider";
import { useGlobalModal } from "@/hooks/useGlobalModal";
import { retrieveMatterQueryOptions } from "@/lib/data/react-query/matters/matter";
import { useUpdateMatter } from "@/lib/data/react-query/matters/matter";
import { useCreateMatterStatusPhase } from "@/lib/data/react-query/matters/matter-status";
import {
  retrievePinnedMattersQueryOptions,
  useCreateMatterPinned,
  useDeleteMatterPinned,
} from "@/lib/data/react-query/matters/matter-pinned";
import { retrieveMatterSettingsQueryOptions } from "@/lib/data/react-query/matters/matter-settings";
import type { MatterSearchResult } from "@/lib/data/requests/matters/search-matters";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import {
  getMatterStatusCategoryScope,
  getMatterStatusCategoryLabel,
  type MatterStatusCategoryOption,
} from "@/lib/data/static/matterStatics";
import {
  copyLinkToClipboard,
  copyToClipboard,
} from "@/lib/utils/copyToClipboard";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/lib/data/static/query-keys";
import {
  CheckSquare,
  Clipboard,
  ExternalLink,
  FileText,
  Link,
  ListTodo,
  Mail,
  NotebookPen,
  Pin,
  PinOff,
  Plus,
  Share2,
} from "lucide-react";
import { useMemo, useState, type ReactNode } from "react";

type MatterContextMenuMatter = Pick<
  MatterSearchResult,
  "attorneyFee" | "dateModified" | "emailAddress" | "id" | "statusId" | "title"
> & {
  status?: { categoryId?: string; id: string; label: string } | null;
};

type MatterRowContextMenuProps = {
  actions?: ReactNode;
  allowedStatusCategories?: readonly MatterStatusCategoryOption[];
  onOpenChange?: (open: boolean) => void;
  trigger: ReactNode;
} & ({ matter: MatterContextMenuMatter } | { matterId: string });

export function MatterRowContextMenu(props: MatterRowContextMenuProps) {
  const { actions, allowedStatusCategories, onOpenChange, trigger } = props;
  const initialMatter = "matter" in props ? props.matter : undefined;
  const matterId = "matterId" in props ? props.matterId : props.matter.id;
  const queryClient = useQueryClient();
  const { currentUser, hasActionPermission } = useUser();
  const { openNoteModal, openTaskModal } = useGlobalModal();
  const [activePanel, setActivePanel] = useState<"notes" | "tasks">();
  const [statusSearch, setStatusSearch] = useState("");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { data: matterData, isError: isMatterError } = useQuery({
    ...retrieveMatterQueryOptions(matterId),
    enabled: Boolean(activePanel) || (!initialMatter && isMenuOpen),
  });
  const matter = initialMatter ?? matterData?.data;
  const { data: matterSettingsData } = useQuery(
    retrieveMatterSettingsQueryOptions(),
  );
  const createMatterStatusPhase = useCreateMatterStatusPhase();
  const updateMatterFee = useUpdateMatter({
    successToast: "Attorney fee updated successfully",
  });
  const createMatterPinned = useCreateMatterPinned();
  const deleteMatterPinned = useDeleteMatterPinned();
  const { data: pinnedMattersData } = useQuery({
    ...retrievePinnedMattersQueryOptions(),
    enabled: isMenuOpen,
  });
  const canChangeStatus = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterStatusPhase,
    ACTION_PERMISSION_VALUES.create,
  );
  const canCreateTasks = hasActionPermission(
    ACTION_PERMISSION_KEYS.matter,
    ACTION_PERMISSION_VALUES.update,
  );
  const canCreateNotes = hasActionPermission(
    ACTION_PERMISSION_KEYS.matter,
    ACTION_PERMISSION_VALUES.update,
  );
  const entityLabel = matter?.title?.trim() || "this matter";
  const matterPath = `/matters/${matterId}`;
  const matterUrl =
    typeof window === "undefined"
      ? matterPath
      : new URL(matterPath, window.location.origin).href;
  const currentPinnedMatter = pinnedMattersData?.data.find(
    (pinnedMatter) =>
      pinnedMatter.matter.id === matterId &&
      pinnedMatter.userId === currentUser.id,
  );
  const isPinnedMutationPending =
    createMatterPinned.isPending || deleteMatterPinned.isPending;
  const statuses = useMemo(() => {
    if (!matter) return [];
    const search = statusSearch.trim().toLowerCase();
    const allStatuses = matterSettingsData?.data.statuses ?? [];
    const scopedCategories =
      allowedStatusCategories ??
      getMatterStatusCategoryScope(matter.status?.categoryId);
    const scopedStatuses = scopedCategories.length
      ? allStatuses.filter((status) =>
          scopedCategories.some((category) => category === status.categoryId),
        )
      : allStatuses;
    return search
      ? scopedStatuses.filter((status) =>
          status.label.toLowerCase().includes(search),
        )
      : scopedStatuses;
  }, [
    allowedStatusCategories,
    matter,
    matterSettingsData?.data.statuses,
    statusSearch,
  ]);
  const handleStatusChange = async (statusId: string) => {
    if (!matter || statusId === matter.statusId) {
      return;
    }
    const selectedStatus = matterSettingsData?.data.statuses.find(
      (status) => status.id === statusId,
    );
    try {
      const res = await createMatterStatusPhase.mutateAsync({
        dateTimeStart: new Date().toISOString(),
        matterId,
        statusId,
      });
      const defaultFee =
        selectedStatus?.defaultAttorneyFee ??
        res.data.defaultAttorneyFee ??
        null;
      const currentFee =
        matter.attorneyFee == null || matter.attorneyFee === ""
          ? null
          : Number(matter.attorneyFee);
      if (defaultFee !== null && currentFee !== Number(defaultFee)) {
        await updateMatterFee.mutateAsync({
          attorneyFee: defaultFee,
          dateModified: res.data.dateModified,
          id: matterId,
        });
      }
    } catch {
      // The mutation surfaces the API error toast, matching the overview control.
    } finally {
      if (!initialMatter) {
        void queryClient.invalidateQueries({ queryKey: [queryKeys.reports] });
      }
    }
  };
  return (
    <>
      <ContextMenu
        entityName="matter"
        onOpenChange={(open) => {
          setIsMenuOpen(open);
          onOpenChange?.(open);
        }}
        trigger={trigger}
      >
        <ContextMenuItem
          onSelect={() => {
            window.open(
              `/matters/${matterId}`,
              "_blank",
              "noopener,noreferrer",
            );
          }}
        >
          <ExternalLink />
          Open in new tab
        </ContextMenuItem>
        <ContextMenuSub>
          <ContextMenuSubTrigger>
            <Clipboard />
            Copy
          </ContextMenuSubTrigger>
          <ContextMenuSubContent>
            <ContextMenuItem
              disabled={!matter?.emailAddress}
              onSelect={() => {
                void copyToClipboard(
                  matter?.emailAddress ?? "",
                  "Matter email",
                );
              }}
            >
              <Mail />
              Matter email
            </ContextMenuItem>
            <ContextMenuItem
              onSelect={() => {
                void copyToClipboard(matterUrl, "Matter URL");
              }}
            >
              <Link />
              Matter URL
            </ContextMenuItem>
            <ContextMenuItem
              disabled={!matter}
              onSelect={() => {
                void copyLinkToClipboard({
                  label: "Share link",
                  text: matter?.title?.trim() || "Matter",
                  url: matterUrl,
                });
              }}
            >
              <Share2 />
              Share link
            </ContextMenuItem>
          </ContextMenuSubContent>
        </ContextMenuSub>
        <ContextMenuItem
          disabled={isPinnedMutationPending || !pinnedMattersData}
          onSelect={() => {
            if (currentPinnedMatter) {
              void deleteMatterPinned.mutateAsync({
                pinnedMatterId: currentPinnedMatter.id,
              });
              return;
            }

            void createMatterPinned.mutateAsync({ matterId });
          }}
        >
          {currentPinnedMatter ? <PinOff /> : <Pin />}
          {currentPinnedMatter ? "Unpin matter" : "Pin matter"}
        </ContextMenuItem>
        {actions ? <ContextMenuSeparator /> : null}
        {actions}
        {canChangeStatus ? (
          <SearchableContextMenuSubmenu
            emptyMessage={
              isMatterError ? "Unable to load matter." : "No statuses found."
            }
            getItemKey={(status) => status.id}
            getItemGroup={(status) =>
              getMatterStatusCategoryLabel(status.categoryId)
            }
            isLoading={!matterSettingsData || (!matter && !isMatterError)}
            items={statuses}
            onSearchChange={setStatusSearch}
            onSelectItem={(status) => {
              void handleStatusChange(status.id);
            }}
            placeholder="Search statuses..."
            renderItem={(status) => status.label}
            search={statusSearch}
            selectedItemKeys={matter?.statusId ? [matter.statusId] : []}
            title={
              <>
                <CheckSquare />
                Change matter status
              </>
            }
          />
        ) : null}
        <ContextMenuSub>
          <ContextMenuSubTrigger>
            <CheckSquare />
            Tasks
          </ContextMenuSubTrigger>
          <ContextMenuSubContent>
            <ContextMenuItem
              onSelect={() => {
                setActivePanel("tasks");
              }}
            >
              <ListTodo />
              Open tasks
            </ContextMenuItem>
            {canCreateTasks ? (
              <ContextMenuItem
                onSelect={() => {
                  openTaskModal({
                    matterId,
                    editing: false,
                    initialTab: "details",
                    hideMatterField: true,
                  });
                }}
              >
                <Plus />
                Create task
              </ContextMenuItem>
            ) : null}
          </ContextMenuSubContent>
        </ContextMenuSub>
        <ContextMenuSub>
          <ContextMenuSubTrigger>
            <NotebookPen />
            Notes
          </ContextMenuSubTrigger>
          <ContextMenuSubContent>
            <ContextMenuItem
              onSelect={() => {
                setActivePanel("notes");
              }}
            >
              <FileText />
              Open notes
            </ContextMenuItem>
            {canCreateNotes ? (
              <ContextMenuItem
                onSelect={() => {
                  openNoteModal({
                    matterId,
                    editing: false,
                    initialTab: "details",
                  });
                }}
              >
                <Plus />
                Create note
              </ContextMenuItem>
            ) : null}
          </ContextMenuSubContent>
        </ContextMenuSub>
      </ContextMenu>

      {matterData?.data ? (
        <>
          <AppModal
            open={activePanel === "tasks"}
            onOpenChange={(open) => {
              if (!open) setActivePanel(undefined);
            }}
            title={`Tasks for ${entityLabel}`}
            size="3xl"
            bodyContained
          >
            <div className="flex h-[min(70vh,42rem)] min-h-0 flex-col">
              <MatterTasks
                tasks={[]}
                matter={matterData.data}
                defaultVariant="list"
                showCreateButton
                showFilters={false}
                bareLayout
                loadMoreMode="infinite"
              />
            </div>
          </AppModal>
          <AppModal
            open={activePanel === "notes"}
            onOpenChange={(open) => {
              if (!open) setActivePanel(undefined);
            }}
            title={`Notes for ${entityLabel}`}
            size="3xl"
            bodyContained
          >
            <div className="flex h-[min(80vh,52rem)] min-h-0 flex-col p-4">
              <MatterNotes notes={[]} matter={matterData.data} bareLayout />
            </div>
          </AppModal>
        </>
      ) : null}
    </>
  );
}
